import { useState } from 'react'
import { X, CheckCircle2, Info } from 'lucide-react'

interface BankrollMovementModalProps {
  onClose: () => void
  mode: 'deposit' | 'withdraw'
  currentBalance?: number | null
  isPending: boolean
  isError: boolean
  isSuccess: boolean
  onSubmit: (amount: number) => void
  /** Sección 16: DEPOSITAR/RETIRAR es solo contabilidad DENTRO de John Anthony -- nunca mueve
   * dinero bancario real. El texto explicativo es obligatorio, nunca opcional. */
  explainer?: string
}

/** Componente genérico reutilizado por Mi Banca (Sección 16) y Mi Reto (Sección 14) -- misma
 * UX, mismo blindaje contra saldo insuficiente en retiros, mismo texto de "esto no mueve
 * dinero real" en ambos flujos. */
export function BankrollMovementModal({
  onClose,
  mode,
  currentBalance,
  isPending,
  isError,
  isSuccess,
  onSubmit,
  explainer,
}: BankrollMovementModalProps) {
  const [amount, setAmount] = useState('')
  const parsed = Number(amount.replace(',', '.'))
  const insufficientFunds = mode === 'withdraw' && currentBalance != null && parsed > currentBalance
  const canSubmit = parsed > 0 && !insufficientFunds && !isPending

  if (isSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4" onClick={onClose}>
        <div className="card w-full max-w-sm p-6 text-center space-y-3" onClick={(e) => e.stopPropagation()}>
          <CheckCircle2 className="h-10 w-10 mx-auto text-brand" />
          <p className="text-sm font-black text-text">{mode === 'deposit' ? '¡Depósito registrado!' : '¡Retiro registrado!'}</p>
          <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-brand text-black text-xs font-black uppercase">
            Cerrar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-sm p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-black text-text">{mode === 'deposit' ? 'Depositar' : 'Retirar'}</p>
          <button onClick={onClose} className="p-1 text-text-dim hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        {explainer && (
          <div className="flex items-start gap-2 bg-panel-elevated/50 border border-border/40 rounded-xl p-2.5">
            <Info className="h-3.5 w-3.5 text-cyan shrink-0 mt-0.5" />
            <p className="text-[10px] text-text-dim leading-relaxed">{explainer}</p>
          </div>
        )}

        {mode === 'withdraw' && currentBalance != null && (
          <p className="text-[10px] text-text-dim">
            Saldo disponible: <span className="font-mono font-bold text-text">{currentBalance.toFixed(2)}€</span>
          </p>
        )}

        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase text-text-dim">Importe (€)</label>
          <input
            type="number" inputMode="decimal" step="0.01" min="0.01" placeholder="0.00" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-panel-elevated border border-border/60 rounded-xl px-3 py-2.5 text-sm font-mono font-bold text-text focus:outline-none focus:border-brand/50"
          />
        </div>

        {insufficientFunds && (
          <p className="text-[10px] text-danger">Saldo insuficiente para retirar ese importe.</p>
        )}
        {isError && <p className="text-[10px] text-danger">No se pudo registrar el movimiento. Intenta de nuevo.</p>}

        <button
          disabled={!canSubmit}
          onClick={() => onSubmit(parsed)}
          className="w-full py-2.5 rounded-xl bg-brand text-black text-xs font-black uppercase tracking-wider disabled:opacity-40 disabled:pointer-events-none"
        >
          Confirmar {mode === 'deposit' ? 'depósito' : 'retiro'}
        </button>
      </div>
    </div>
  )
}
